import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { FloppyDisk } from '@phosphor-icons/react';

const SETTING_GROUPS = [
  { key: 'general', label: 'General', fields: [
    { key: 'app_name', label: 'App Name', type: 'text' },
    { key: 'default_product_target', label: 'Default Product Target', type: 'text' },
  ]},
  { key: 'discovery', label: 'Discovery', fields: [
    { key: 'default_country', label: 'Default Country', type: 'text' },
    { key: 'default_region', label: 'Default Region', type: 'text' },
    { key: 'search_result_cap', label: 'Search Result Cap', type: 'text' },
    { key: 'dedupe_strictness', label: 'Dedup Strictness', type: 'text' },
  ]},
  { key: 'scoring', label: 'Scoring', fields: [
    { key: 'scoring_mode', label: 'Scoring Mode', type: 'text' },
    { key: 'min_campaign_ready_score', label: 'Min Campaign-Ready Score', type: 'text' },
  ]},
  { key: 'compliance', label: 'Compliance', fields: [
    { key: 'business_emails_only', label: 'Business Emails Only', type: 'toggle' },
    { key: 'exclude_free_providers', label: 'Exclude Free Email Providers', type: 'toggle' },
    { key: 'require_approval_before_export', label: 'Require Approval Before Export', type: 'toggle' },
  ]},
  { key: 'integration', label: 'Pro-Email Integration', fields: [
    { key: 'pro_email_default_tags', label: 'Default Tags', type: 'text' },
  ]},
];

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(r => { setSettings(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', { settings });
      toast.success('Settings saved');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading settings...</div>;

  return (
    <div data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure scoring thresholds, discovery defaults, compliance rules, and integration settings. Changes take effect immediately.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-black text-white hover:bg-gray-800" data-testid="save-settings-btn">
          <FloppyDisk size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <div className="space-y-6">
        {SETTING_GROUPS.map(group => (
          <div key={group.key} className="border border-gray-200 rounded-md bg-white p-6">
            <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-4">{group.label}</h3>
            <div className="space-y-4">
              {group.fields.map(field => (
                <div key={field.key} className="flex items-center justify-between">
                  <label className="text-sm text-gray-700 w-1/3">{field.label}</label>
                  {field.type === 'toggle' ? (
                    <Switch
                      checked={settings[field.key] === 'true'}
                      onCheckedChange={(v) => updateSetting(field.key, v ? 'true' : 'false')}
                      data-testid={`setting-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={settings[field.key] || ''}
                      onChange={e => updateSetting(field.key, e.target.value)}
                      className="w-2/3 bg-white border-gray-300"
                      data-testid={`setting-${field.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
